import { supabase } from "@/lib/supabase";
import {
  ApplyToQuestError,
  ReferCandidateError,
  ReferralLinkError,
  isReferCandidateErrorCode,
  isReferralLinkErrorCode,
} from "@/lib/questErrors";
import type { ApplyToQuestErrorCode } from "@/lib/questErrors";
import type {
  ICandidatesService,
  QuestCandidate,
  ReferCandidateInput,
  WorkerJourney,
} from "@/services/interfaces/ICandidatesService";
import type { ExpressInterestResult } from "@/lib/types";
import { candidateDisplayIdentity } from "@/lib/candidateIdentity";

// The HINT is whatever the database sent; narrow it before trusting it as a
// code, so a migration that adds a refusal without updating the union here
// degrades to "unknown" instead of producing a code the UI cannot render.
const APPLY_ERROR_CODES = [
  "apply_quest_unavailable",
  "apply_own_quest",
  "apply_rate_limited",
  "apply_no_profile",
] as const satisfies readonly ApplyToQuestErrorCode[];

function isApplyErrorCode(hint: string | null | undefined): hint is ApplyToQuestErrorCode {
  return !!hint && (APPLY_ERROR_CODES as readonly string[]).includes(hint);
}

// Shape of a row from the creator-facing RPC (R1): no email/phone, no
// linkedin_url, and no source column are returned — the creator never sees
// sourced contact data or off-platform identity. Origin/conversion is admin-only.
interface QuestCandidateRow {
  id: string;
  member_id: string | null;
  full_name: string | null;
  location_city: string | null;
  location_country: string | null;
  skills: string[] | null;
  employment_title: string | null;
  score: number | null;
  availability_answer: QuestCandidate["availabilityAnswer"];
  referred_by_name: string | null;
  /** Absent until migration 20261002140000 is applied. */
  profile_id?: string | null;
}

/** Row shape from `worker_journey_state` — journey fields only, no PII. */
interface WorkerJourneyRow {
  candidate_id: string;
  status: WorkerJourney["status"];
  availability_answer: WorkerJourney["availabilityAnswer"];
  applied_at: string | null;
  selected_at: string | null;
}

function mapRow(row: QuestCandidateRow): QuestCandidate {
  return {
    id: row.id,
    memberId: row.member_id,
    // Falls back to member_id so this still works if the frontend ships
    // before the migration adds profile_id.
    profileId: row.profile_id ?? row.member_id,
    // A sourced row can carry full_name = "" (which `??` lets through); the
    // creator view already falls back to the member's display name, so a blank
    // here means nobody named them. Only the name is passed: R1 keeps contact
    // data off this path.
    fullName: candidateDisplayIdentity({ fullName: row.full_name }),
    locationCity: row.location_city,
    locationCountry: row.location_country,
    skills: row.skills ?? [],
    employmentTitle: row.employment_title,
    score: row.score != null ? Number(row.score) : null,
    availabilityAnswer: row.availability_answer ?? null,
    referredByName: row.referred_by_name ?? null,
  };
}

export class SupabaseCandidatesService implements ICandidatesService {
  async listForQuest(questId: string): Promise<QuestCandidate[]> {
    // R1: call the contact-free creator function, not the base quest_candidates
    // table. The function is SECURITY DEFINER with SET search_path = public —
    // it scopes to the caller's quests and surfaces only internal + converted rows.
    const { data, error } = await supabase
      .rpc("quest_candidates_creator_v", { p_quest_id: questId })
      .order("score", { ascending: false });

    if (error) throw new Error(error.message);
    // Supabase can return data=null with error=null when RLS filters every row
    // (see SupabaseQuestService) — coalesce so .map never hits null.
    return ((data ?? []) as QuestCandidateRow[]).map(mapRow);
  }

  async journeyForQuest(questId: string): Promise<WorkerJourney | null> {
    // SECURITY DEFINER, scoped to converted_user_id = requesting_user_id(). The
    // quest id is NOT the credential — passing someone else's quest id returns
    // the caller's row on it, or nothing.
    const { data, error } = await supabase.rpc("worker_journey_state", {
      p_quest_id: questId,
    });
    if (error) throw new Error(error.message);

    const row = ((data ?? []) as WorkerJourneyRow[])[0];
    if (!row) return null;
    return {
      candidateId: row.candidate_id,
      status: row.status,
      availabilityAnswer: row.availability_answer ?? null,
      appliedAt: row.applied_at,
      selectedAt: row.selected_at,
    };
  }

  async applyToQuest(questId: string, referralCode?: string | null): Promise<string> {
    // SECURITY DEFINER keyhole: `authenticated` has no INSERT or UPDATE on
    // quest_candidates (20260903130000 §4b) and this does not change that. The
    // applicant is `requesting_user_id()` inside the function, which is why
    // there is no user id in this call to get wrong — and the REFERRER is
    // likewise resolved from the code inside the function, not sent from here.
    // The referral key is OMITTED, not sent as null, when there is no code.
    //
    // PostgREST resolves an RPC by its exact argument-NAME set, so a request
    // carrying `p_referral_code` only matches the two-argument signature that
    // 20260909110000 introduces. Sending it unconditionally would mean that
    // between the frontend deploy and `supabase db push` — a real window, since
    // Vercel deploys on merge while migrations are applied by hand — EVERY
    // application on the platform would 404 with PGRST202, not just referred
    // ones. Omitting the key lets an un-referred apply resolve against either
    // signature, so the funnel survives the ordering.
    //
    // A referred apply still needs the migration, which is sound: referral
    // codes cannot exist before the migration that creates the links table.
    const { data, error } = await supabase.rpc(
      "worker_apply_to_quest",
      referralCode ? { p_quest_id: questId, p_referral_code: referralCode } : { p_quest_id: questId },
    );
    // The refusals are deliberately coarse — "cannot apply to this quest" covers
    // both a missing quest and a draft/finished one, so the id cannot be used to
    // probe for other creators' drafts. Keep that shape; only translate the
    // Postgres error into something the UI can localise. The code travels in the
    // HINT so the message can stay a developer-facing English string (one of
    // them names a table) that no user ever sees.
    if (error) {
      throw new ApplyToQuestError(
        isApplyErrorCode(error.hint) ? error.hint : "unknown",
        error.message,
      );
    }
    return data as string;
  }

  async expressInterestInQuest(
    questId: string,
    referralCode?: string | null,
  ): Promise<ExpressInterestResult> {
    // Same argument-shape discipline as applyToQuest above: PostgREST resolves
    // an RPC by its exact argument-NAME set, and the frontend deploys on merge
    // while migrations are applied by hand. Omitting the referral key — rather
    // than sending null — lets an un-referred call resolve even if a future
    // migration adds another overload.
    const { data, error } = await supabase.rpc(
      "worker_express_interest_in_quest",
      referralCode ? { p_quest_id: questId, p_referral_code: referralCode } : { p_quest_id: questId },
    );

    // The refusals are worker_apply_to_quest's, unchanged: coarse messages with
    // the actionable code in the HINT. Reuse ApplyToQuestError so
    // applyErrorMessage keeps working with no new mapping.
    if (error) {
      throw new ApplyToQuestError(
        isApplyErrorCode(error.hint) ? error.hint : "unknown",
        error.message,
      );
    }

    // RETURNS TABLE arrives as a one-row array through PostgREST.
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) {
      throw new ApplyToQuestError("unknown", "express interest returned no row");
    }

    return {
      candidateId: row.candidate_id as string,
      tasksAccepted: Number(row.tasks_accepted ?? 0),
      tasksSkipped: Number(row.tasks_skipped ?? 0),
    };
  }

  async expressInterestInTasks(
    questId: string,
    taskIds: string[],
    referralCode?: string | null,
  ): Promise<ExpressInterestResult> {
    // Same argument-shape discipline as expressInterestInQuest above: PostgREST
    // resolves an RPC by its exact argument-NAME set, so the referral key is
    // omitted rather than sent as null when there is no code.
    const { data, error } = await supabase.rpc(
      "worker_express_interest_in_tasks",
      referralCode
        ? { p_quest_id: questId, p_task_ids: taskIds, p_referral_code: referralCode }
        : { p_quest_id: questId, p_task_ids: taskIds },
    );

    if (error) {
      throw new ApplyToQuestError(
        isApplyErrorCode(error.hint) ? error.hint : "unknown",
        error.message,
      );
    }

    // RETURNS TABLE arrives as a one-row array through PostgREST.
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) {
      throw new ApplyToQuestError("unknown", "express interest in tasks returned no row");
    }

    return {
      candidateId: row.candidate_id as string,
      tasksAccepted: Number(row.tasks_accepted ?? 0),
      tasksSkipped: Number(row.tasks_skipped ?? 0),
    };
  }

  async select(candidateId: string): Promise<string> {
    const { data, error } = await supabase.rpc("creator_select_quest_candidate", {
      p_candidate_id: candidateId,
    });
    // The RPC raises one message for "not yours", "not found" and "wrong
    // status" so a creator cannot probe another creator's funnel. Passing it
    // through unchanged keeps that property — do not try to enrich it here.
    if (error) throw new Error(error.message);
    return data as string;
  }

  async createReferralLink(questId: string): Promise<string> {
    const { data, error } = await supabase.rpc("create_quest_referral_link", {
      p_quest_id: questId,
    });
    // Same coarse-refusal shape as applyToQuest: one message covers a missing
    // quest and a draft one, so the id cannot probe for other creators' drafts.
    // The code travels in the HINT so the message can stay developer-facing —
    // which only holds if someone actually READS the hint. Throwing a bare
    // Error(error.message) here (as this did until now) put untranslated
    // Postgres text on the only path the UI had, collapsing "finish your
    // profile" — the one refusal a member can act on — into a generic retry.
    // `referral_link_revoked` is PERMANENT and must not be shown under
    // "please try again" copy either.
    if (error) {
      throw new ReferralLinkError(
        isReferralLinkErrorCode(error.hint) ? error.hint : "unknown",
        error.message,
      );
    }
    return data as string;
  }

  async referCandidate(input: ReferCandidateInput): Promise<void> {
    const { error } = await supabase.rpc("worker_refer_candidate", {
      p_quest_id: input.questId,
      p_full_name: input.fullName,
      p_email: input.email,
      p_phone: input.phone,
      p_note: input.note,
    });
    // The code is in the HINT, as with createReferralLink. The RPC returns VOID
    // and raises only about the referrer, so there is no result to read.
    if (error) {
      throw new ReferCandidateError(
        isReferCandidateErrorCode(error.hint) ? error.hint : "unknown",
        error.message,
      );
    }
  }
}
